'use client';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, Building, TrendingUp, DollarSign, Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface DashboardProps {
  onNavigate?: (module: string) => void;
}

export function Dashboard({ onNavigate }: DashboardProps) {
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [newJob, setNewJob] = useState({ title: '', department: '', purpose: '' });
  const statsCards = [
    { title: '전체 직무', value: '247개', change: '+12', icon: Building, color: 'blue' },
    { title: '평가 완료', value: '189개', change: '+8', icon: TrendingUp, color: 'green' },
    { title: '급여 밴드', value: '15개', change: '±0', icon: DollarSign, color: 'purple' },
    { title: '워크숍', value: '3개', change: '+1', icon: Calendar, color: 'orange' }
  ];

  const jobDistributionData = [
    { name: '개발부', count: 45 },
    { name: '영업부', count: 32 },
    { name: '마케팅', count: 28 },
    { name: '인사부', count: 15 },
    { name: '재무부', count: 8 }
  ];

  const salaryBandData = [
    { band: 'L1', min: 3000, max: 4000, current: 3500, count: 25 },
    { band: 'L2', min: 4000, max: 5500, current: 4800, count: 42 },
    { band: 'L3', min: 5500, max: 7000, current: 6200, count: 38 },
    { band: 'L4', min: 7000, max: 9000, current: 8100, count: 31 },
    { band: 'L5', min: 9000, max: 12000, current: 10500, count: 18 }
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const ongoingWorkshops = [
    { title: '직무분석 워크숍 - 개발팀', date: '2024.01.15', participants: 12, status: '진행중' },
    { title: '직무평가 위원회 회의', date: '2024.01.18', participants: 8, status: '예정' },
    { title: '급여체계 시뮬레이션 검토', date: '2024.01.20', participants: 15, status: '예정' }
  ];

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {statsCards.map((stat, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm whitespace-nowrap">{stat.title}</CardTitle>
              <stat.icon className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="text-lg sm:text-2xl font-bold">{stat.value}</span>
                <Badge variant={stat.change.startsWith('+') ? 'default' : 'secondary'} className="text-[10px] sm:text-xs px-1 sm:px-2">
                  {stat.change}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Job Distribution Chart */}
        <Card>
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="text-sm sm:text-base">부서별 직무 분포</CardTitle>
            <CardDescription className="text-xs sm:text-sm">전체 직무의 부서별 분포</CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={jobDistributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={60}
                  fill="#8884d8"
                  dataKey="count"
                  fontSize={10}
                >
                  {jobDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Salary Band Chart */}
        <Card>
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="text-sm sm:text-base">급여 밴드 현황</CardTitle>
            <CardDescription className="text-xs sm:text-sm">직무등급별 급여 범위</CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={salaryBandData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="band" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(value, name) => [value + '만원', name === 'current' ? '평균급여' : name]} />
                <Bar dataKey="min" stackId="a" fill="#e2e8f0" />
                <Bar dataKey="current" stackId="b" fill="#3b82f6" />
                <Bar dataKey="max" stackId="a" fill="#cbd5e1" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Ongoing Workshops */}
      <Card>
        <CardHeader className="p-3 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
            <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>진행 중인 워크숍</span>
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">직무 관련 주요 활동</CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0">
          <div className="space-y-2 sm:space-y-4">
            {ongoingWorkshops.map((workshop, index) => (
              <div key={index} className="flex items-center justify-between p-2 sm:p-4 border rounded-lg gap-2">
                <div className="min-w-0">
                  <h4 className="font-medium text-xs sm:text-sm truncate">{workshop.title}</h4>
                  <p className="text-[10px] sm:text-sm text-gray-500">{workshop.date} · {workshop.participants}명</p>
                </div>
                <Badge variant={workshop.status === '진행중' ? 'default' : 'secondary'} className="text-[10px] sm:text-xs flex-shrink-0">
                  {workshop.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="p-3 sm:p-6">
          <CardTitle className="text-sm sm:text-base">빠른 작업</CardTitle>
          <CardDescription className="text-xs sm:text-sm">주요 기능에 바로 접근</CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
            <Button variant="outline" className="h-14 sm:h-20 flex flex-col gap-1 sm:gap-2 p-2" onClick={() => setIsJobModalOpen(true)}>
              <Building className="h-4 w-4 sm:h-6 sm:w-6" />
              <span className="text-[10px] sm:text-sm whitespace-nowrap">새 직무 등록</span>
            </Button>
            <Button variant="outline" className="h-14 sm:h-20 flex flex-col gap-1 sm:gap-2 p-2" onClick={() => onNavigate?.('job-evaluation')}>
              <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6" />
              <span className="text-[10px] sm:text-sm whitespace-nowrap">직무평가</span>
            </Button>
            <Button variant="outline" className="h-14 sm:h-20 flex flex-col gap-1 sm:gap-2 p-2" onClick={() => onNavigate?.('salary-design')}>
              <DollarSign className="h-4 w-4 sm:h-6 sm:w-6" />
              <span className="text-[10px] sm:text-sm whitespace-nowrap">급여 시뮬</span>
            </Button>
            <Button variant="outline" className="h-14 sm:h-20 flex flex-col gap-1 sm:gap-2 p-2" onClick={() => onNavigate?.('hr-integration')}>
              <Users className="h-4 w-4 sm:h-6 sm:w-6" />
              <span className="text-[10px] sm:text-sm whitespace-nowrap">워크숍 등록</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 새 직무 등록 모달 */}
      <Dialog open={isJobModalOpen} onOpenChange={setIsJobModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">새 직무 등록</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">새로운 직무를 등록합니다.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:gap-4 py-2 sm:py-4">
            <div className="space-y-1 sm:space-y-2">
              <Label htmlFor="job-title" className="text-xs sm:text-sm">직무명 *</Label>
              <Input
                id="job-title"
                placeholder="예: 백엔드 개발자"
                value={newJob.title}
                onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
                className="text-sm"
              />
            </div>
            <div className="space-y-1 sm:space-y-2">
              <Label htmlFor="job-dept" className="text-xs sm:text-sm">부서 *</Label>
              <Select value={newJob.department} onValueChange={(v) => setNewJob({ ...newJob, department: v })}>
                <SelectTrigger id="job-dept" className="text-sm">
                  <SelectValue placeholder="부서 선택" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="개발부">개발부</SelectItem>
                  <SelectItem value="영업부">영업부</SelectItem>
                  <SelectItem value="마케팅부">마케팅부</SelectItem>
                  <SelectItem value="인사부">인사부</SelectItem>
                  <SelectItem value="재무부">재무부</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 sm:space-y-2">
              <Label htmlFor="job-purpose" className="text-xs sm:text-sm">직무 목적</Label>
              <Textarea
                id="job-purpose"
                placeholder="이 직무의 주요 목적을 입력하세요"
                value={newJob.purpose}
                onChange={(e) => setNewJob({ ...newJob, purpose: e.target.value })}
                rows={3}
                className="text-sm"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsJobModalOpen(false)} className="text-xs sm:text-sm">취소</Button>
            <Button onClick={() => {
              setIsJobModalOpen(false);
              setNewJob({ title: '', department: '', purpose: '' });
            }} className="text-xs sm:text-sm">등록</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
